import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useUser } from 'app/user';
import {
  getSubscription,
  postCancelSubscription,
  postChangePlanPreview,
  postChangePlan,
} from 'utils/api';

const SubscriptionManage = () => {
  const navigate = useNavigate();
  const { plan: currentPlan, updatePlan } = useUser();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [changePlanLoading, setChangePlanLoading] = useState(false);

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const res = await getSubscription();
      if (res.statusCode === 200) {
        setSubscription(res.results);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (
      !window.confirm(
        '구독을 해지하시겠습니까?\n만료일까지 서비스를 이용하실 수 있습니다.',
      )
    )
      return;

    try {
      const res = await postCancelSubscription();
      if (res.statusCode === 200) {
        if (res.results?.legacyNotice) {
          alert(res.results.legacyNotice);
        }
        alert('구독이 해지되었습니다.');
        fetchSubscription();
      }
    } catch (err) {
      alert(err.response?.data?.message || '해지 처리 중 오류가 발생했습니다.');
    }
  };

  const handleChangePlanPreview = async (newPlan) => {
    try {
      setChangePlanLoading(true);
      const res = await postChangePlanPreview({ newPlan });
      if (res.statusCode === 200) {
        setPreview(res.results);
      }
    } catch (err) {
      alert(
        err.response?.data?.message ||
          '플랜 변경 정보를 불러오는 중 오류가 발생했습니다.',
      );
    } finally {
      setChangePlanLoading(false);
    }
  };

  const handleChangePlanConfirm = async () => {
    if (!preview) return;

    try {
      setChangePlanLoading(true);
      const res = await postChangePlan({ newPlan: preview.newPlan });
      if (res.statusCode === 200) {
        if (preview.type === 'upgrade') {
          alert(
            `업그레이드 완료! 차액 ${res.results.proratedAmount.toLocaleString()}원이 결제되었습니다.`,
          );
        } else {
          alert('다운그레이드가 적용되었습니다.');
        }
        setPreview(null);
        updatePlan(preview.newPlan);
      }
    } catch (err) {
      alert(
        err.response?.data?.message || '플랜 변경 처리 중 오류가 발생했습니다.',
      );
    } finally {
      setChangePlanLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ko-KR');
  };

  if (loading) {
    return (
      <Container>
        <Card>
          <Title>구독 관리</Title>
          <p>로딩 중...</p>
        </Card>
      </Container>
    );
  }

  if (!subscription || !subscription.plan) {
    return (
      <Container>
        <Card>
          <Title>구독 관리</Title>
          <InfoGrid>
            <InfoItem>
              <InfoLabel>현재 플랜</InfoLabel>
              <InfoValue>
                <PlanBadge plan="free">FREE</PlanBadge>
              </InfoValue>
            </InfoItem>
          </InfoGrid>
          <EmptyState>
            <p>무료 플랜을 이용 중입니다.</p>
            <p style={{ fontSize: '13px', marginTop: '6px' }}>
              더 많은 지역과 분석 기능을 이용하려면 유료 플랜을 구독해보세요.
            </p>
            <ActionButton
              style={{ marginTop: '24px' }}
              onClick={() => navigate('/pricing')}
            >
              요금제 보기
            </ActionButton>
          </EmptyState>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <Title>구독 관리</Title>

        <InfoGrid>
          <InfoItem>
            <InfoLabel>현재 플랜</InfoLabel>
            <InfoValue>
              <PlanBadge plan={subscription.plan}>
                {subscription.plan?.toUpperCase()}
              </PlanBadge>
            </InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>상태</InfoLabel>
            <InfoValue>
              <StatusBadge status={subscription.status}>
                {subscription.status === 'active'
                  ? '활성'
                  : subscription.status === 'cancelled'
                  ? '해지됨'
                  : '만료'}
              </StatusBadge>
            </InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>월 결제 금액</InfoLabel>
            <InfoValue>{subscription.amount?.toLocaleString()}원</InfoValue>
          </InfoItem>
          {!subscription.legacy && (
            <InfoItem>
              <InfoLabel>결제 카드</InfoLabel>
              <InfoValue>
                {subscription.cardName} {subscription.cardNo}
              </InfoValue>
            </InfoItem>
          )}
          {subscription.currentPeriodEnd && (
            <InfoItem>
              <InfoLabel>
                {subscription.legacy ? '만료일' : '현재 구독 기간'}
              </InfoLabel>
              <InfoValue>
                {subscription.legacy
                  ? formatDate(subscription.currentPeriodEnd)
                  : `${formatDate(
                      subscription.currentPeriodStart,
                    )} ~ ${formatDate(subscription.currentPeriodEnd)}`}
              </InfoValue>
            </InfoItem>
          )}
          {subscription.cancelledAt && (
            <InfoItem>
              <InfoLabel>해지 요청일</InfoLabel>
              <InfoValue>{formatDate(subscription.cancelledAt)}</InfoValue>
            </InfoItem>
          )}
        </InfoGrid>

        {subscription.legacy && (
          <LegacyNotice>
            기존 외부 결제로 관리되는 구독입니다. 플랜 변경 또는 해지 시 기존
            외부 정기결제는 결제 사이트에서 직접 해제해주세요.
          </LegacyNotice>
        )}

        <ButtonGroup>
          {subscription.status === 'active' && (
            <>
              {currentPlan === 'basic' && (
                <ActionButton
                  onClick={() =>
                    subscription.legacy
                      ? navigate('/subscribe/premium')
                      : handleChangePlanPreview('premium')
                  }
                  disabled={changePlanLoading}
                >
                  PREMIUM 업그레이드
                </ActionButton>
              )}
              {currentPlan === 'premium' && !subscription.legacy && (
                <DowngradeButton
                  onClick={() => handleChangePlanPreview('basic')}
                  disabled={changePlanLoading}
                >
                  BASIC 다운그레이드
                </DowngradeButton>
              )}
              <CancelButton onClick={handleCancel}>구독 해지</CancelButton>
            </>
          )}
          {subscription.status === 'cancelled' && (
            <ActionButton onClick={() => navigate('/pricing')}>
              재구독하기
            </ActionButton>
          )}
          <SecondaryButton onClick={() => navigate('/pricing')}>
            요금제 비교
          </SecondaryButton>
        </ButtonGroup>
      </Card>

      {preview && (
        <ModalOverlay onClick={() => setPreview(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>
              {preview.type === 'upgrade'
                ? '플랜 업그레이드'
                : '플랜 다운그레이드'}
            </ModalTitle>

            <ModalInfoGrid>
              <ModalInfoItem>
                <ModalInfoLabel>변경 플랜</ModalInfoLabel>
                <ModalInfoValue>
                  {preview.currentPlan?.toUpperCase()} →{' '}
                  <strong>{preview.newPlan?.toUpperCase()}</strong>
                </ModalInfoValue>
              </ModalInfoItem>

              {preview.type === 'upgrade' && (
                <>
                  <ModalInfoItem>
                    <ModalInfoLabel>남은 기간</ModalInfoLabel>
                    <ModalInfoValue>{preview.remainingDays}일</ModalInfoValue>
                  </ModalInfoItem>
                  <ModalInfoItem highlight>
                    <ModalInfoLabel>차액 결제 금액</ModalInfoLabel>
                    <ModalInfoValue style={{ color: '#644ded' }}>
                      {preview.proratedAmount?.toLocaleString()}원
                    </ModalInfoValue>
                  </ModalInfoItem>
                </>
              )}

              <ModalInfoItem>
                <ModalInfoLabel>
                  {preview.type === 'upgrade'
                    ? '현재 구독 만료일'
                    : '적용 시점'}
                </ModalInfoLabel>
                <ModalInfoValue>
                  {preview.type === 'upgrade'
                    ? formatDate(preview.currentPeriodEnd)
                    : `${formatDate(preview.currentPeriodEnd)} (다음 결제일)`}
                </ModalInfoValue>
              </ModalInfoItem>

              <ModalInfoItem>
                <ModalInfoLabel>변경 후 월 결제 금액</ModalInfoLabel>
                <ModalInfoValue>
                  {preview.newMonthlyAmount?.toLocaleString()}원/월
                </ModalInfoValue>
              </ModalInfoItem>
            </ModalInfoGrid>

            {preview.type === 'downgrade' && (
              <ModalNotice>
                다운그레이드는 현재 구독 기간 만료 후 다음 결제일부터
                적용됩니다. 만료일까지 현재 플랜의 기능을 이용하실 수 있습니다.
              </ModalNotice>
            )}

            <ModalButtonGroup>
              <ActionButton
                onClick={handleChangePlanConfirm}
                disabled={changePlanLoading}
              >
                {changePlanLoading
                  ? '처리 중...'
                  : preview.type === 'upgrade'
                  ? `${preview.proratedAmount?.toLocaleString()}원 결제하기`
                  : '다운그레이드 확인'}
              </ActionButton>
              <SecondaryButton onClick={() => setPreview(null)}>
                취소
              </SecondaryButton>
            </ModalButtonGroup>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

const Container = styled.div`
  max-width: 520px;
  margin: 0 auto;
  padding: 60px 20px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 40px 32px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #232d42;
  margin-bottom: 32px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 0;
  color: #8f9bba;
`;

const InfoGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 32px;
`;

const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
`;

const InfoLabel = styled.span`
  font-size: 14px;
  color: #8f9bba;
`;

const InfoValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #232d42;
`;

const PlanBadge = styled.span`
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 12px;
  color: #fff;
  background-color: ${({ plan }) =>
    plan === 'premium' ? '#ff6b35' : plan === 'free' ? '#8f9bba' : '#644ded'};
`;

const StatusBadge = styled.span`
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 12px;
  color: #fff;
  background-color: ${({ status }) =>
    status === 'active'
      ? '#38a169'
      : status === 'cancelled'
      ? '#e53e3e'
      : '#a3aed0'};
`;

const LegacyNotice = styled.div`
  background-color: #fef9e7;
  border: 1px solid #f0e6b2;
  border-radius: 8px;
  padding: 14px 16px;
  font-size: 13px;
  color: #7a6c2a;
  margin-bottom: 20px;
  line-height: 1.5;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ActionButton = styled.button`
  width: 100%;
  padding: 14px 0;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  background-color: #644ded;
  cursor: pointer;

  &:hover {
    background-color: #5338c9;
  }

  &:disabled {
    background-color: #a3aed0;
    cursor: not-allowed;
  }
`;

const DowngradeButton = styled.button`
  width: 100%;
  padding: 14px 0;
  border: 1.5px solid #ff6b35;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #ff6b35;
  background: transparent;
  cursor: pointer;

  &:hover {
    background-color: #fff8f5;
  }

  &:disabled {
    border-color: #a3aed0;
    color: #a3aed0;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  width: 100%;
  padding: 14px 0;
  border: 1.5px solid #e53e3e;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #e53e3e;
  background: transparent;
  cursor: pointer;

  &:hover {
    background-color: #fff5f5;
  }
`;

const SecondaryButton = styled.button`
  width: 100%;
  padding: 14px 0;
  border: 1.5px solid #e4e4e4;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #8f9bba;
  background: transparent;
  cursor: pointer;

  &:hover {
    border-color: #644ded;
    color: #644ded;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  max-width: 440px;
  width: 100%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.16);
`;

const ModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #232d42;
  margin-bottom: 24px;
`;

const ModalInfoGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
`;

const ModalInfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
  ${({ highlight }) =>
    highlight &&
    `
    background: #f8f6ff;
    padding: 10px 12px;
    border-radius: 8px;
    border-bottom: none;
  `}
`;

const ModalInfoLabel = styled.span`
  font-size: 14px;
  color: #8f9bba;
`;

const ModalInfoValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #232d42;
`;

const ModalNotice = styled.div`
  background-color: #fff8f0;
  border: 1px solid #ffe0c0;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 13px;
  color: #8a5a2a;
  margin-bottom: 20px;
  line-height: 1.5;
`;

const ModalButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export default SubscriptionManage;
